export type Update =
  | { name: "primaryVoltagekV"; value: number }
  | { name: "autoSelect"; value: boolean }
  | { name: "nameplateRating"; value: "auto" };

export interface TransformerType {
  rating: number;
}

export interface ReconcileArgs {
  /** Transformer types available at the currently selected Primary Voltage (kV).
   * Pass an empty array when none exist.
   */
  transformerTypesForPrimaryVoltage: TransformerType[];

  /** Current selected nameplate rating (kVA) */
  nameplateRating: number;

  /** Whether “Auto Select” is currently enabled */
  autoSelect: boolean;

  /** Mounting style (e.g. "gmt" | "pmt") */
  mounting: string;

  /** Reference data containing transformer defaults */
  reference: {
    transformers: {
      defaults: Record<string, number>;
    };
  };
}

/**
 * Decide what updates are needed when the selected Primary Voltage or nameplate
 * rating become invalid. Returns an ordered list of updates for the caller to apply.
 */
export function reconcilePrimaryVoltageAndRating({
  transformerTypesForPrimaryVoltage,
  nameplateRating,
  autoSelect,
  mounting,
  reference,
}: ReconcileArgs): Update[] {
  // If no transformer types exist at the chosen Primary Voltage, revert to the default Primary Voltage.
  if (!transformerTypesForPrimaryVoltage || transformerTypesForPrimaryVoltage.length === 0) {
    const fallbackVoltage = reference.transformers.defaults[`${mounting}DefaultPrimaryVoltagekV`];
    return [{ name: "primaryVoltagekV", value: fallbackVoltage }];
  }

  // If the previously selected rating is missing and Auto Select is off, switch to Auto.
  const ratingNum = Number.parseInt(String(nameplateRating), 10);
  const ratingStillExists = transformerTypesForPrimaryVoltage.some((t) => t.rating === ratingNum);

  if (!autoSelect && !ratingStillExists) {
    return [
      { name: "autoSelect", value: true },
      { name: "nameplateRating", value: "auto" },
    ];
  }

  // No changes required
  return [];
}
